import { Fragment, useState, type ReactNode } from 'react';
import { cn } from '@/shared/utils/cn';

interface OverflowListProps<T> {
  items: T[];
  maxVisible: number;
  renderItem: (item: T, index: number) => ReactNode;
  getKey: (item: T, index: number) => string | number;
  className?: string;
}

export function OverflowList<T>({
  items,
  maxVisible,
  renderItem,
  getKey,
  className,
}: OverflowListProps<T>) {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = items.length - maxVisible;
  const hasOverflow = hiddenCount > 0;
  const visibleItems = expanded || !hasOverflow ? items : items.slice(0, maxVisible);

  return (
    <div className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1.5', className)}>
      {visibleItems.map((item, index) => (
        <Fragment key={getKey(item, index)}>{renderItem(item, index)}</Fragment>
      ))}
      {hasOverflow && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Show fewer' : `Show ${hiddenCount} more`}
          className="rounded-md border border-transparent px-1.5 py-0.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          {expanded ? '− Show less' : `+${hiddenCount}`}
        </button>
      )}
    </div>
  );
}
